import { HeartHandshake, Network, MapPin, ClipboardList, Users, Building2 } from "lucide-react";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";

const LOST_TIME = [
  { icon: HeartHandshake, label: "Client engagement" },
  { icon: Network, label: "Care coordination" },
  { icon: MapPin, label: "Outreach" },
  { icon: ClipboardList, label: "Treatment planning" },
  { icon: Users, label: "Team collaboration" },
  { icon: Building2, label: "Community-based services" },
];

export function Problem() {
  return (
    <section id="problem" className="bg-slate-50 py-20 lg:py-28" aria-labelledby="problem-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-2 translate-x-4 translate-y-4 rounded-2xl bg-navy" aria-hidden="true" />
              <img
                src="https://images.unsplash.com/photo-1706806595208-0e823368f240?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="A community health professional meeting with a client outside a clinical setting"
                loading="lazy"
                className="relative aspect-[4/3] w-full rounded-2xl object-cover shadow-2xl"
              />
            </div>
          </Reveal>

          <div>
            <Reveal>
              <Eyebrow>The Operational Problem</Eyebrow>
              <SectionHeading id="problem-heading">
                Your Team Was Built for the Community — Not the Keyboard
              </SectionHeading>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
                Healthcare professionals spend substantial time documenting encounters after services are delivered.
                Every additional minute spent formatting a note is time unavailable for client engagement, outreach,
                or care coordination.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="problem-lost-time-list">
                {LOST_TIME.map((item) => (
                  <li key={item.label} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                    <item.icon className="h-5 w-5 shrink-0 text-teal-600" aria-hidden="true" />
                    <span className="text-sm font-medium text-navy">{item.label}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 border-l-4 border-teal-600 bg-white p-5">
                <p className="text-sm leading-relaxed text-slate-600">
                  <span className="font-semibold text-navy">VeraChart solves this without another expensive monthly
                  software subscription.</span> Your team keeps the AI platform your agency already trusts — we supply
                  the prompt architecture and the training that make it produce audit-ready documentation.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
